import React, { useCallback, useContext, useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import { S_DEVICE } from '@components/lpc/Constants';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import UserContext from '@components/lpc/Context/User/UserContext';
import AddMessageForm from '@components/lpc/Pot/MainModule/Informations/GreetingCard/AddMessageForm/AddMessageForm';
import BannerMessage from '@components/lpc/Pot/MainModule/Informations/GreetingCard/BannerMessage/BannerMessage';
import GreetingCardAction from '@components/lpc/Pot/MainModule/Informations/GreetingCard/GreetingCardAction/GreetingCardAction';
import { personRoles } from '@models/PotModel/personRoles';
import { greetingCardInformationsType } from '@propTypes/potTypes';
import greetingCardService from '@services/domain/Lpc/GreetingCardService';
import { getRoute, ROUTE } from '@services/http/Route';
import ObjectService, { isEmpty } from '@services/utils/ObjectService';

import css from './GreetingCard.module.scss';

type GreetingCardProps = {
    personRole: string;
    potRef: string;
    hasMessage: boolean;
    messagesCount: number;
    hideHeader?: boolean;
    potCategory: string;
    isOffered: boolean;
    onGetGreetingCardMessages?: () => void;
    orderId?: string;
};

const GreetingCard = ({
    hasMessage,
    personRole,
    potRef,
    messagesCount,
    hideHeader,
    potCategory,
    isOffered,
    orderId,
    onGetGreetingCardMessages,
}: GreetingCardProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { width } = useWindowSize();

    const router = useRouter();
    const { addToast } = useToasts();
    const userContext = useContext(UserContext);
    const { userAccount } = userContext;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasLeftMessage, setHasLeftMessage] = useState<boolean>(hasMessage);
    const [isGreetingCardVisible, setIsGreetingCardVisible] = useState<boolean>(true);
    const [openMessageForm, setOpenMessageForm] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [isLoginModalVisible, setIsLoginModalVisible] = useState<boolean>(false);
    const [greetingCardInformations, setGreetingCardInformations] = useState<greetingCardInformationsType>({
        isGreetingCardDisabled: false,
        hasDarkThemedGreetingCard: false,
        hasAnimatedGreetingCardPO: false,
    });
    const [myMessage, setMyMessage] = useState<{ message?: string; inviteID: number }>(null);
    const [showPreviewButton, setShowPreviewButton] = useState<boolean>(
        !greetingCardInformations.isGreetingCardDisabled &&
            messagesCount > 0 &&
            personRole !== personRoles.BENEFICIARY &&
            width < S_DEVICE,
    );

    useEffect(() => {
        setIsGreetingCardVisible(
            personRole === personRoles.ORGANISER || !greetingCardInformations.isGreetingCardDisabled,
        );
    }, [greetingCardInformations.isGreetingCardDisabled, personRole]);

    useEffect(() => {
        greetingCardService
            .getGreetingCardInformations(potRef)
            .then((res) =>
                setGreetingCardInformations({
                    isGreetingCardDisabled: res.disableGreetingCardPO,
                    hasDarkThemedGreetingCard: res.hasDarkThemedGreetingCard,
                    hasAnimatedGreetingCardPO: res.hasAnimatedGreetingCardPO,
                }),
            )
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    }, [addToast, potRef]);

    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const getMyMessage = useCallback(() => {
        greetingCardService
            .getMyMessage(potRef, orderId)
            .then((res) => {
                setHasLeftMessage(ObjectService.hasContent(res) && res.message);
                setMyMessage(res);
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    }, [getUpdatedPot, potRef]);

    useEffect(() => {
        getMyMessage();
    }, [getMyMessage]);

    const onToggleActivation = (bool) => {
        setIsLoading(true);
        const body = {
            disableGreetingCardPO: bool,
            hasDarkThemedGreetingCard: greetingCardInformations.hasDarkThemedGreetingCard,
            hasAnimatedGreetingCardPO: greetingCardInformations.hasAnimatedGreetingCardPO,
        };

        return greetingCardService
            .update(potRef, body)
            .then((res) =>
                setGreetingCardInformations({
                    ...greetingCardInformations,
                    isGreetingCardDisabled: res.data.disableGreetingCardPO,
                }),
            )
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => {
                setIsLoading(false);
                setShowPreviewButton(!showPreviewButton);
            });
    };

    const onDeleteMessage = () => {
        greetingCardService
            .deleteMessage(potRef, myMessage.inviteID, orderId)
            .then(() => {
                getUpdatedPot(potRef);
                getMyMessage();
                onGetGreetingCardMessages && onGetGreetingCardMessages();
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => {
                setIsDeleteModalVisible(false);
                addToast(t(`success.MESSAGE_DELETED`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            });
    };

    const onOpenMessageForm = () => {
        isEmpty(userAccount) && !orderId ? setIsLoginModalVisible(true) : setOpenMessageForm(true);
    };

    const showPreview = () => {
        if (showPreviewButton) {
            router.push(
                getRoute(
                    ROUTE.LPC.POT.POT,
                    potRef,
                    potCategory === 'BEREAVEMENT' ? ROUTE.LPC.POT.BEREAVEMENT_CARD : ROUTE.LPC.POT.GREETING_CARD,
                ),
            );
        }
    };

    return (
        <>
            <AddMessageForm
                onGetGreetingCardMessages={onGetGreetingCardMessages}
                isVisible={openMessageForm}
                onHide={() => {
                    setOpenMessageForm(false);
                    getMyMessage();
                }}
                potRef={potRef}
                message={!isEmpty(myMessage) && myMessage.message ? myMessage.message : ''}
                potCategory={potCategory}
                orderId={orderId}
            />

            <ConfirmModal
                onHide={() => setIsDeleteModalVisible(false)}
                onConfirm={() => onDeleteMessage()}
                title={t('greeting-card.modal.delete-title')}
                text={t('greeting-card.modal.delete-text')}
                confirmLabel={t('greeting-card.modal.delete-confirm')}
                cancelLabel={t('greeting-card.modal.delete-cancel')}
                isVisible={isDeleteModalVisible}
                iconComponent={<IconCheckmark variant={'warning'} />}
            />

            <ConfirmModal
                onHide={() => setIsLoginModalVisible(false)}
                onConfirm={() => router.push(getRoute(ROUTE.LPC.AUTH.SIGN_IN, `?source=`, router.asPath))}
                title={t('greeting-card.modal.login-title')}
                text={t('greeting-card.modal.login-text')}
                confirmLabel={t('greeting-card.modal.login-confirm')}
                isVisible={isLoginModalVisible}
                iconComponent={<IconCheckmark variant={'spy'} />}
            />

            {isGreetingCardVisible && (
                <div className={css.greeting}>
                    <div className={`${css.greeting__header} ${hideHeader && css.greeting__header__hidden}`}>
                        <div className={css.greeting__header__left} onClick={() => showPreviewButton && showPreview()}>
                            <h5>
                                {potCategory !== 'BEREAVEMENT' && potCategory !== 'SOLIDARITY_POOL'
                                    ? t('greeting-card.title')
                                    : t('greeting-card.title-bereavement')}
                            </h5>
                            {messagesCount > 0 && <h5>&nbsp;({messagesCount})</h5>}
                        </div>

                        <section className={css.greeting__header__right}>
                            {!greetingCardInformations.isGreetingCardDisabled &&
                                messagesCount > 0 &&
                                personRole !== personRoles.BENEFICIARY && (
                                    <Link
                                        href={getRoute(
                                            ROUTE.LPC.POT.POT,
                                            potRef,
                                            potCategory === 'BEREAVEMENT'
                                                ? ROUTE.LPC.POT.BEREAVEMENT_CARD
                                                : ROUTE.LPC.POT.GREETING_CARD,
                                        )}
                                    >
                                        <Button variant={'white'} customClass={css.greeting__header__right__preview}>
                                            {width > S_DEVICE
                                                ? t('greeting-card.preview')
                                                : t('greeting-card.mobile-preview')}
                                        </Button>
                                    </Link>
                                )}
                            {personRole === personRoles.ORGANISER && !isOffered && (
                                <GreetingCardAction
                                    onActivate={(bool) => onToggleActivation(bool)}
                                    onDeActivate={(bool) => onToggleActivation(bool)}
                                    showPreview={() => showPreview()}
                                    showPreviewButton={showPreviewButton}
                                    potRef={potRef}
                                    personRole={personRole}
                                    isActivated={!greetingCardInformations.isGreetingCardDisabled}
                                    isLoading={isLoading}
                                    hasDarkThemedGreetingCard={greetingCardInformations.hasDarkThemedGreetingCard}
                                    hasAnimatedGreetingCardPO={greetingCardInformations.hasAnimatedGreetingCardPO}
                                    potCategory={potCategory}
                                />
                            )}
                        </section>
                    </div>

                    <BannerMessage
                        potRef={potRef}
                        personRole={personRole}
                        isLoading={isLoading}
                        hasMessage={hasLeftMessage}
                        isActivated={!greetingCardInformations.isGreetingCardDisabled}
                        onOpenMessageForm={() => onOpenMessageForm()}
                        messagesCount={messagesCount}
                        onDeleteMessage={() => setIsDeleteModalVisible(true)}
                    />
                </div>
            )}
        </>
    );
};
export default GreetingCard;
